import { Image, Input, Modal, Row, Form, Button, Select } from 'antd'
import React, { useMemo } from 'react'
import './ModalUserDetail.style.less'
import { isEmptyValue } from '@/utils/common'
import { useMutation, useQuery } from 'react-query'
import { updateRoles } from '@/screens/SystemConfigure/UserManagment/UserManagment.transform'
import { useAuth } from '@/providers/auth.provider'
import { queryClient } from '@/config/react-query'
import { find, toLower, capitalize, toUpper } from 'lodash'
import {
  USER_SETTINGS_TYPE,
  USER_STATUS,
  COMPANY_SETTING_HAS_SUPPLIER,
} from '@/constant/user'
import { getSupplierRef } from '@/screens/QSF/Inspection/Inspection.service'
import { getCompanySetting } from '@/screens/SystemConfigure/UserManagment/UserManagment.transform'
import { PBF_QUERY_KEY } from '@/constant/pbf'

interface Props {
  visible: boolean
  onClose: () => void
  dataRoles: any
  data: any
  settings: USER_SETTINGS_TYPE
}

function ModalUserDetail(props: Props) {
  const { user } = useAuth()

  const { dataRoles, settings } = props
  const { enableActivation = false } = settings

  const [form] = Form.useForm()
  const mutation = useMutation(async (data: any) => updateRoles(data))
  const isOwnerAccount = user?.id === props.data?.id

  const handleInitRole = () => {
    const role_id = props.data.subscriptions?.[0]?.role_access?.id

    const findRole = find(dataRoles, {
      value: role_id,
    })
    return findRole?.value
  }

  const selectedRole = Form.useWatch('role', form)

  const isSupplierRole = useMemo(() => {
    const roleId = selectedRole || handleInitRole()
    const roleItem = find(dataRoles, { value: roleId })
    return toUpper(roleItem?.label) === 'SUPPLIER'
  }, [selectedRole, dataRoles])

  const currentSupplier = props.data?.suppliers?.[0]
  const initialSupplierOption = currentSupplier
    ? [{ label: currentSupplier.name, value: currentSupplier.id }]
    : []

  const { data: supplierOptions = initialSupplierOption } = useQuery(
    ['SUPPLIERS_LIST', user?.company?.id],
    async () => {
      const data = await getSupplierRef(
        { companyId: user?.company?.id, type: 'supplier' },
        true
      )
      return (
        data?.map((item: any) => ({ label: item.name, value: item.id })) || []
      )
    },
    { enabled: isSupplierRole }
  )

  const { data: companySettingHasSupplier } = useQuery(
    [PBF_QUERY_KEY.GET_COMPANY_SETTING_HAS_SUPPLIER, user?.company?.id],
    async () => {
      return await getCompanySetting('', COMPANY_SETTING_HAS_SUPPLIER)
    }
  )

  const hasSupplierSetting = companySettingHasSupplier?.value?.status

  const filteredRoles = useMemo(() => {
    const roles = Array.isArray(dataRoles) ? dataRoles : []
    if (!hasSupplierSetting) {
      return roles.filter((role: any) => toUpper(role.label) !== 'SUPPLIER')
    }
    return roles
  }, [dataRoles, hasSupplierSetting])

  const onSave = async () => {
    if (isOwnerAccount) {
      return
    }

    try {
      await form.validateFields()
    } catch (e) {
      return
    }

    const role = form.getFieldValue('role')
    const userStatus = form.getFieldValue('status')
    const supplierId = form.getFieldValue('supplier_id')

    const payload: any = {
      user: props.data.id,
      role_access: role,
    }

    if (isSupplierRole && supplierId) {
      payload.suppliers = [supplierId]
    } else {
      payload.suppliers = []
    }

    if (enableActivation) {
      payload.status = toLower(userStatus)
    }

    await mutation.mutateAsync(payload)
    await queryClient.refetchQueries(['Users'])
    props.onClose()
  }
  const handleRenderingStatus = () => {
    const { ACTIVE, INACTIVE } = USER_STATUS
    const dataStatus = [
      { label: 'Active', value: toLower(ACTIVE) },
      { label: 'Inactive', value: toLower(INACTIVE) },
    ]

    if (enableActivation) {
      return (
        <Form.Item
          label="Status"
          initialValue={props.data?.status}
          name="status"
        >
          <Select disabled={isOwnerAccount} options={dataStatus} />
        </Form.Item>
      )
    }

    return (
      <Form.Item
        label="Status"
        initialValue={capitalize(props.data?.status)}
        name="status"
      >
        <Input disabled size="large" placeholder="Text" />
      </Form.Item>
    )
  }

  return (
    <Modal
      open={props.visible}
      onCancel={props.onClose}
      width={660}
      footer={false}
      className="modal-user-detail-container"
    >
      <Row>
        <Image className="image-profile" src={props?.data?.avatar?.url_path} />
        <div className="detail-container">
          <Form labelWrap={true} labelCol={{ span: 5 }} form={form}>
            <Form.Item
              label="First Name"
              initialValue={props.data.first_name}
              name="first_name"
            >
              <Input disabled size="large" placeholder="Text" />
            </Form.Item>
            <Form.Item
              label="Last Name"
              initialValue={props.data.last_name}
              name="last_name"
            >
              <Input disabled size="large" placeholder="Text" />
            </Form.Item>
            <Form.Item
              label="Email"
              initialValue={props.data.email}
              name="email"
            >
              <Input disabled size="large" placeholder="Text" />
            </Form.Item>
            <Form.Item
              label="Phone"
              initialValue={props.data.phone_number}
              name="phone_number"
            >
              <Input disabled size="large" placeholder="Text" />
            </Form.Item>
            <Form.Item label="Role" initialValue={handleInitRole()} name="role">
              <Select disabled={isOwnerAccount} options={filteredRoles} />
            </Form.Item>
            {isSupplierRole && (
              <Form.Item
                label="Supplier"
                name="supplier_id"
                initialValue={props.data?.suppliers?.[0]?.id}
                required={false}
                rules={[{ required: true, message: 'Supplier is required' }]}
              >
                <Select
                  disabled={isOwnerAccount}
                  options={supplierOptions}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select"
                />
              </Form.Item>
            )}
            {handleRenderingStatus()}
          </Form>
        </div>
      </Row>
      <Row className="button-container">
        <Button onClick={props.onClose} size="large">
          Cancel
        </Button>
        <Button
          disabled={isOwnerAccount}
          size="large"
          type="primary"
          onClick={onSave}
        >
          {!isEmptyValue(props.data) ? 'Save' : 'Create'}
        </Button>
      </Row>
    </Modal>
  )
}

export default ModalUserDetail
